import { useQuery } from "@tanstack/react-query";
import { fetchMyProductAccess } from "../../lib/api/product-access";
import { fetchOrganizations } from "../../lib/api/users";
import { useOrganizationContextOptional } from "../../lib/organization-context";

export type ProductTargetOrganization = { id: string; name: string };

/**
 * Organizations the caller may create products in or transfer products to (edit tier or higher).
 */
export function useProductTargetOrganizations(token: string | null, enabled: boolean) {
  const orgContext = useOrganizationContextOptional();
  const availableOrgs = orgContext?.availableOrgs ?? [];

  const query = useQuery({
    queryKey: ["product-target-organizations", availableOrgs.map((o) => o.id).join(",")],
    enabled: Boolean(token) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<ProductTargetOrganization[]> => {
      const me = await fetchMyProductAccess(token!);
      if (me.isSuperAdmin) {
        const orgs = await fetchOrganizations(token!);
        return orgs
          .map((o) => ({ id: o.id, name: o.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
      }
      const editable = new Set(
        me.organizationTiers
          .filter((o) => o.tier === "edit" || o.tier === "full")
          .map((o) => o.organizationId),
      );
      return availableOrgs
        .filter((o) => editable.has(o.id))
        .map((o) => ({ id: o.id, name: o.name }));
    },
  });

  return { organizations: query.data ?? [], loading: query.isLoading };
}
